import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Brain, MessageSquare, Sparkles, ArrowUpRight, Send } from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

export default function AiAdvisor() {
    const [questions, setQuestions] = useState([]);
    const [custom, setCustom] = useState("");
    const [busy, setBusy] = useState(false);
    const [answer, setAnswer] = useState(null);

    useEffect(() => {
        api.get("/edi/advisor/questions")
            .then((r) => setQuestions(r.data.questions || []))
            .catch(() => toast.error("Failed to load AI Advisor questions"));
    }, []);

    const ask = async (question) => {
        setBusy(true); setAnswer(null);
        try {
            const { data } = await api.post("/edi/advisor/answer", { question });
            setAnswer(data);
        } catch { toast.error("Advisor answer failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-8" data-testid={EDI.aiAdvisorRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        AI Executive Advisor™
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        Ask the boardroom-quality questions — every answer is evidence-backed
                        and links directly to the underlying dashboards.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Brain className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">Evidence-backed · drill-down</span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {questions.map((q) => (
                    <button
                        key={q.key}
                        data-testid={EDI.aiAdvisorPreset(q.key)}
                        onClick={() => ask(q.question)}
                        disabled={busy}
                        className="text-left glass hover-lift rounded-xl p-5 relative overflow-hidden transition-transform duration-200 disabled:opacity-60"
                    >
                        <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                        <MessageSquare className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <div className="mt-3 font-display font-black text-white text-[15px] tracking-tight leading-snug">
                            {q.question}
                        </div>
                    </button>
                ))}
            </div>

            <div className="glass rounded-xl p-6">
                <div className="kicker text-slate-500 text-[10px] mb-2">Or ask your own executive question</div>
                <div className="flex gap-2">
                    <input
                        data-testid={EDI.aiAdvisorCustomInput}
                        value={custom}
                        onChange={(e) => setCustom(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && custom.trim()) { ask(custom); } }}
                        placeholder="e.g. Which projects should concern me today?"
                        className="flex-1 rounded-md bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm font-body text-slate-100 outline-none focus:border-[color:var(--sgsb-gold)]"
                    />
                    <button
                        data-testid={EDI.aiAdvisorCustomSubmit}
                        onClick={() => custom.trim() && ask(custom)}
                        disabled={busy || !custom.trim()}
                        className="inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-4 py-2 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-40 transition-colors duration-200"
                    >
                        <Send className="h-3.5 w-3.5" strokeWidth={2} />
                        Ask
                    </button>
                </div>
            </div>

            {busy && <div className="kicker text-slate-500">Composing evidence-backed answer…</div>}

            {answer && (
                <div className="glass rounded-xl p-6 relative overflow-hidden" data-testid={EDI.aiAdvisorAnswer}>
                    <div className="absolute inset-0 opacity-40 pointer-events-none"
                         style={{ background: "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.22), transparent 55%)" }} />
                    <div className="relative">
                        <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                            <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                            <span className="kicker text-[10px]">Executive Advisor · {answer.source === "canned" ? "primary answer" : "closest match"}</span>
                        </div>
                        <h2 className="mt-2 font-display font-black text-white text-xl tracking-tight">
                            {answer.matched_question || answer.question}
                        </h2>
                        <p className="mt-3 text-slate-200 font-body text-base leading-relaxed max-w-4xl">
                            {answer.answer}
                        </p>

                        <div className="mt-5">
                            <div className="kicker text-slate-500 text-[10px] mb-2">Evidence</div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {answer.evidence.map((e, i) => (
                                    <Link key={i} to={e.link}
                                          className="rounded-md border border-slate-800 bg-slate-950/40 p-3 hover:border-[color:var(--sgsb-gold)]/40 transition-colors duration-150"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm text-slate-100 font-body">{e.label}</div>
                                            <ArrowUpRight className="h-3 w-3 text-[var(--sgsb-gold)]" strokeWidth={2} />
                                        </div>
                                        <div className="mt-1 text-[var(--sgsb-gold)] font-display font-bold text-sm">{e.value}</div>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <div className="mt-5">
                            <div className="kicker text-slate-500 text-[10px] mb-2">Drill-down</div>
                            <div className="flex flex-wrap gap-2">
                                {answer.drilldown.map((d) => (
                                    <span key={d} className="rounded-full border border-slate-700 bg-slate-950/40 text-slate-300 text-xs px-3 py-1">{d}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
